import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ReferenceArea,
} from 'recharts';
import {
  applyInfraFilters,
  buildFilterLabel,
  distinctRowsBy,
  formatInfraMonth,
  groupByMonth,
  groupSum,
  isInfraClosedInPeriod,
  normalizeDisplayValue,
  normalizeNumber,
  sameFilter,
} from '../utils/infraAnalytics';
import { useInfraestruturaData } from '../context/InfraestruturaDataContext';
import InfraEvolutionNotice from './InfraEvolutionNotice';

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const darkPanelClassName =
  'rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] p-4 shadow-[0_14px_34px_rgba(15,23,42,0.08)]';

const chartTooltipStyle = {
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  color: 'var(--text-primary)',
  boxShadow: '0 18px 40px rgba(15,23,42,0.12)',
};

const SummaryCard = ({ title, value, hint }) => (
  <div className={darkPanelClassName}>
    <p className="text-[11px] font-semibold uppercase tracking-[0.10em] text-[color:var(--text-muted)]">{title}</p>
    <p className="mt-2 text-4xl font-semibold leading-none text-[color:var(--text-primary)]">{value}</p>
    {hint ? <p className="mt-2 text-xs text-[color:var(--text-muted)]">{hint}</p> : null}
  </div>
);

const ClickableList = ({ items, onClick, activeField, filters }) => (
  <div className="space-y-2">
    {items.map((item) => {
      const isActive = Array.isArray(filters)
        && filters.some((filter) => sameFilter(filter, {
          field: activeField,
          value: item.value,
        }));

      return (
        <button
          key={`${activeField}:${String(item.value ?? 'null')}`}
          type="button"
          onClick={() => onClick(item)}
          className={`flex w-full items-center justify-between gap-3 rounded-[12px] border px-3 py-2 text-left text-sm transition-colors ${
            isActive
              ? 'border-[color:var(--accent)] bg-[color:var(--surface-muted)]/90 text-[color:var(--text-primary)]'
              : 'border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)] hover:border-[color:var(--header-control-border-subtle)] hover:bg-[color:var(--surface-muted)]/70'
          }`}
        >
          <span className="min-w-0 truncate">{item.label}</span>
          <span className="shrink-0 font-semibold text-[color:var(--accent)]">{item.valueFormatted}</span>
        </button>
      );
    })}
  </div>
);

const ValoresDashboard = ({ isMobile = false }) => {
  const {
    rowsRaw,
    rowsFiltered,
    evolutionMonths,
    filters,
    toggleFilter,
    selectHistoricalMonth,
    activeRangeMonth,
  } = useInfraestruturaData();

  const closedRows = useMemo(() => rowsFiltered.filter((row) => isInfraClosedInPeriod(row)), [rowsFiltered]);
  const rowsWithoutSubjectFilter = useMemo(
    () => applyInfraFilters(rowsRaw, filters.filter((filter) => filter.field !== 'assunto')),
    [filters, rowsRaw],
  );
  const rowsWithoutReferenceFilter = useMemo(
    () => applyInfraFilters(rowsRaw, filters.filter((filter) => filter.field !== 'estrutura_referencia_normalizada')),
    [filters, rowsRaw],
  );
  const rowsWithoutCityFilter = useMemo(
    () => applyInfraFilters(rowsRaw, filters.filter((filter) => filter.field !== 'cidade_nome')),
    [filters, rowsRaw],
  );
  const rowsWithoutStructureFilter = useMemo(
    () => applyInfraFilters(rowsRaw, filters.filter((filter) => filter.field !== 'estrutura_descricao')),
    [filters, rowsRaw],
  );
  const rowsWithoutProjectFilter = useMemo(
    () => applyInfraFilters(rowsRaw, filters.filter((filter) => filter.field !== 'projeto_nome')),
    [filters, rowsRaw],
  );
  const closedRowsWithoutSubjectFilter = useMemo(
    () => rowsWithoutSubjectFilter.filter((row) => isInfraClosedInPeriod(row)),
    [rowsWithoutSubjectFilter],
  );
  const closedRowsWithoutReferenceFilter = useMemo(
    () => rowsWithoutReferenceFilter.filter((row) => isInfraClosedInPeriod(row)),
    [rowsWithoutReferenceFilter],
  );
  const closedRowsWithoutCityFilter = useMemo(
    () => rowsWithoutCityFilter.filter((row) => isInfraClosedInPeriod(row)),
    [rowsWithoutCityFilter],
  );
  const closedRowsWithoutStructureFilter = useMemo(
    () => rowsWithoutStructureFilter.filter((row) => isInfraClosedInPeriod(row)),
    [rowsWithoutStructureFilter],
  );
  const closedRowsWithoutProjectFilter = useMemo(
    () => rowsWithoutProjectFilter.filter((row) => isInfraClosedInPeriod(row)),
    [rowsWithoutProjectFilter],
  );
  const uniqueRows = useMemo(() => distinctRowsBy(closedRows, 'id_os'), [closedRows]);
  const monthlySeries = useMemo(() => {
    if (Array.isArray(evolutionMonths) && evolutionMonths.length > 0) {
      return evolutionMonths.map((item) => ({
        label: formatInfraMonth(item?.ano_mes || 'Não informado'),
        rawMonth: item?.ano_mes || 'Não informado',
        count: Number(item?.finalizadas ?? item?.total_os ?? 0),
        total: Number(item?.total_valor ?? 0),
        rompimentos: Number(item?.rompimentos ?? 0),
        media_horas: Number(item?.media_horas ?? 0),
      }));
    }

    return groupByMonth(closedRows, 'total_valor');
  }, [closedRows, evolutionMonths]);

  const kpis = useMemo(() => {
    const totalValor = closedRows.reduce((acc, row) => acc + normalizeNumber(row?.total_valor), 0);
    const totalOs = uniqueRows.length;
    const custoMedio = totalOs > 0 ? totalValor / totalOs : 0;
    const projetosDistintos = new Set(
      closedRows
        .map((row) => normalizeDisplayValue(row?.projeto_nome))
        .filter((value) => value !== 'Não informado'),
    ).size;

    return {
      totalValor,
      totalOs,
      custoMedio,
      projetosDistintos,
    };
  }, [closedRows, uniqueRows]);

  const rankingAssunto = useMemo(
    () => groupSum(closedRowsWithoutSubjectFilter, 'assunto', 'total_valor').slice(0, 10),
    [closedRowsWithoutSubjectFilter],
  );
  const rankingReferencia = useMemo(
    () => groupSum(closedRowsWithoutReferenceFilter, 'estrutura_referencia_normalizada', 'total_valor').slice(0, 10),
    [closedRowsWithoutReferenceFilter],
  );
  const rankingCidade = useMemo(
    () => groupSum(closedRowsWithoutCityFilter, 'cidade_nome', 'total_valor').slice(0, 10),
    [closedRowsWithoutCityFilter],
  );
  const rankingEstrutura = useMemo(
    () => groupSum(closedRowsWithoutStructureFilter, 'estrutura_descricao', 'total_valor').slice(0, 10),
    [closedRowsWithoutStructureFilter],
  );
  const rankingProjeto = useMemo(
    () => groupSum(closedRowsWithoutProjectFilter, 'projeto_nome', 'total_valor').slice(0, 10),
    [closedRowsWithoutProjectFilter],
  );

  const handleFilter = (field, value) => {
    toggleFilter({
      field,
      value: value === 'Não informado' ? null : value,
      label: buildFilterLabel(field, value === 'Não informado' ? null : value),
      ...(field === 'ano_mes_fechamento' ? { eventType: 'fechamento' } : {}),
    });
  };

  const handleMonthSelect = (payload) => {
    const candidate = payload?.activePayload?.[0]?.payload
      || payload?.payload
      || payload;

    const rawMonth = candidate?.rawMonth || candidate?.label;

    if (rawMonth && rawMonth !== 'Não informado') {
      selectHistoricalMonth(rawMonth);
    }
  };

  const chartTickProps = isMobile ? { fontSize: 10 } : { fontSize: 11 };
  const chartMargin = isMobile
    ? { top: 8, right: 8, left: -14, bottom: 8 }
    : { top: 8, right: 20, left: 0, bottom: 8 };
  const xAxisProps = isMobile
    ? { interval: 'preserveStartEnd', minTickGap: 22, tickMargin: 8 }
    : { interval: 0, minTickGap: 10, tickMargin: 6 };
  const legendWrapperStyle = isMobile ? { paddingTop: 12, fontSize: 11 } : undefined;

  return (
    <div className="mt-4 w-full min-h-[720px] space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-4">
        <SummaryCard title="Custo total" value={currencyFormatter.format(kpis.totalValor)} hint="Soma de total_valor" />
        <SummaryCard title="OS finalizadas" value={kpis.totalOs.toLocaleString('pt-BR')} hint="Contagem por id_os" />
        <SummaryCard title="Custo médio por OS" value={currencyFormatter.format(kpis.custoMedio)} hint="Base em O.S. finalizadas" />
        <SummaryCard title="Projetos informados" value={kpis.projetosDistintos.toLocaleString('pt-BR')} />
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
        <div className={`${darkPanelClassName} xl:col-span-8`}>
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Evolução mensal de custo</h3>
              {activeRangeMonth ? (
                <span className="rounded-full border border-[color:var(--accent)]/35 bg-[color:var(--accent)]/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[color:var(--accent)]">
                  Mês ativo: {formatInfraMonth(activeRangeMonth)}
                </span>
              ) : null}
            </div>
            <span className="text-[11px] text-[color:var(--text-muted)]">Clique no mês para atualizar o período</span>
          </div>
          <InfraEvolutionNotice />
          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlySeries} margin={chartMargin} onClick={handleMonthSelect} barCategoryGap={isMobile ? '24%' : '16%'}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.20)" />
                {activeRangeMonth ? (
                  <ReferenceArea
                    x1={formatInfraMonth(activeRangeMonth)}
                    x2={formatInfraMonth(activeRangeMonth)}
                    fill="rgba(59,130,246,0.10)"
                    strokeOpacity={0}
                  />
                ) : null}
                <XAxis dataKey="label" tick={chartTickProps} stroke="var(--text-muted)" {...xAxisProps} />
                <YAxis tick={chartTickProps} width={isMobile ? 34 : 48} stroke="var(--text-muted)" />
                <Tooltip
                  contentStyle={chartTooltipStyle}
                  labelStyle={{ color: 'var(--text-primary)' }}
                  itemStyle={{ color: 'var(--text-primary)' }}
                  formatter={(value) => currencyFormatter.format(Number(value || 0))}
                />
                <Legend wrapperStyle={legendWrapperStyle} iconSize={isMobile ? 10 : 14} />
                <Bar
                  dataKey="total"
                  name="Custo"
                  fill="#2563eb"
                  radius={[6, 6, 0, 0]}
                  onClick={(payload) => {
                    const item = payload?.payload || payload;
                    if (item?.rawMonth || item?.label) {
                      const rawMonth = item?.rawMonth || item?.label;
                      if (rawMonth && rawMonth !== 'Não informado') {
                        selectHistoricalMonth(rawMonth);
                      }
                    }
                  }}
                >
                  {monthlySeries.map((item) => {
                    const isActive = item?.rawMonth === activeRangeMonth;
                    return (
                      <Cell
                        key={`custo:${item?.rawMonth || item?.label}`}
                        fill={isActive ? '#60a5fa' : '#2563eb'}
                        stroke={isActive ? '#bfdbfe' : 'transparent'}
                        strokeWidth={isActive ? 2 : 0}
                      />
                    );
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={`${darkPanelClassName} xl:col-span-4`}>
          <h3 className="mb-3 text-sm font-semibold text-[color:var(--text-primary)]">Custo por cidade</h3>
          <ClickableList
            items={rankingCidade.map((item) => ({
              label: item.label,
              value: item.label,
              valueFormatted: currencyFormatter.format(item.total),
            }))}
            onClick={(item) => handleFilter('cidade_nome', item.label)}
            activeField="cidade_nome"
            filters={filters}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <div className={darkPanelClassName}>
          <h3 className="mb-3 text-sm font-semibold text-[color:var(--text-primary)]">Custo por assunto</h3>
          <ClickableList
            items={rankingAssunto.map((item) => ({
              label: item.label,
              value: item.label,
              valueFormatted: currencyFormatter.format(item.total),
            }))}
            onClick={(item) => handleFilter('assunto', item.label)}
            activeField="assunto"
            filters={filters}
          />
        </div>

        <div className={darkPanelClassName}>
          <h3 className="mb-3 text-sm font-semibold text-[color:var(--text-primary)]">Custo por referência</h3>
          <ClickableList
            items={rankingReferencia.map((item) => ({
              label: item.label,
              value: item.label,
              valueFormatted: currencyFormatter.format(item.total),
            }))}
            onClick={(item) => handleFilter('estrutura_referencia_normalizada', item.label)}
            activeField="estrutura_referencia_normalizada"
            filters={filters}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <div className={darkPanelClassName}>
          <h3 className="mb-3 text-sm font-semibold text-[color:var(--text-primary)]">Custo por estrutura</h3>
          <ClickableList
            items={rankingEstrutura.map((item) => ({
              label: item.label,
              value: item.label,
              valueFormatted: currencyFormatter.format(item.total),
            }))}
            onClick={(item) => handleFilter('estrutura_descricao', item.label)}
            activeField="estrutura_descricao"
            filters={filters}
          />
        </div>

        <div className={darkPanelClassName}>
          <h3 className="mb-3 text-sm font-semibold text-[color:var(--text-primary)]">Custo por projeto</h3>
          <ClickableList
            items={rankingProjeto.map((item) => ({
              label: item.label,
              value: item.label,
              valueFormatted: currencyFormatter.format(item.total),
            }))}
            onClick={(item) => handleFilter('projeto_nome', item.label)}
            activeField="projeto_nome"
            filters={filters}
          />
        </div>
      </div>
    </div>
  );
};

export default ValoresDashboard;
